const TOKEN_STORAGE_KEYS = [
  "authToken",
  "jwt",
  "jwttoken",
  "jwt_token",
  "token",
  "accesstoken",
  "access_token",
] as const;

const AUTH_FLAG_KEY = "parking-box:auth-flag";
const STORAGE_STRATEGY_KEY = "parking-box:auth-storage";
const PERMISSION_KEY = "parking-box:permission-level";
const USER_ID_KEY = "parking-box:user-id";
const AUTH_EVENT = "parking-box:auth-changed";

type StorageStrategy = "local" | "session";

const isBrowser = typeof window !== "undefined";

const getStorage = (strategy: StorageStrategy): Storage | null => {
  if (!isBrowser) {
    return null;
  }
  try {
    return strategy === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
};

const getStorages = (): Storage[] => {
  const storages: Storage[] = [];
  const local = getStorage("local");
  const session = getStorage("session");
  if (local) {
    storages.push(local);
  }
  if (session) {
    storages.push(session);
  }
  return storages;
};

const readValue = (storage: Storage, key: string): string | null => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const removeValue = (storage: Storage, key: string) => {
  try {
    storage.removeItem(key);
  } catch {
    // ignore storage errors
  }
};

const writeValue = (storage: Storage, key: string, value: string) => {
  try {
    storage.setItem(key, value);
  } catch {
    // ignore storage errors
  }
};

const normalizePermission = (value: number | null | undefined): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, Math.floor(value));
  }
  return null;
};

const normalizeUserId = (value: string | number | null | undefined): string | null => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return null;
};

const dispatchAuthEvent = () => {
  if (!isBrowser) {
    return;
  }
  const event = new Event(AUTH_EVENT);
  window.dispatchEvent(event);
};

export const waitForAuthChanges = (listener: () => void) => {
  if (!isBrowser) {
    return () => undefined;
  }

  const handleEvent = () => listener();
  const handleStorage = (event: StorageEvent) => {
    if (
      event.key &&
      !TOKEN_STORAGE_KEYS.includes(event.key as (typeof TOKEN_STORAGE_KEYS)[number]) &&
      event.key !== AUTH_FLAG_KEY &&
      event.key !== STORAGE_STRATEGY_KEY &&
      event.key !== PERMISSION_KEY &&
      event.key !== USER_ID_KEY
    ) {
      return;
    }
    listener();
  };

  window.addEventListener(AUTH_EVENT, handleEvent);
  window.addEventListener("storage", handleStorage);

  return () => {
    window.removeEventListener(AUTH_EVENT, handleEvent);
    window.removeEventListener("storage", handleStorage);
  };
};

export const readStoredAuthToken = (): string | null => {
  const storages = getStorages();
  for (const storage of storages) {
    for (const key of TOKEN_STORAGE_KEYS) {
      const value = readValue(storage, key);
      if (value) {
        return value;
      }
    }
  }
  return null;
};

export const hasStoredAuthFlag = (): boolean => {
  const storages = getStorages();
  return storages.some((storage) => readValue(storage, AUTH_FLAG_KEY) === "true");
};

export const readStoredStrategy = (): StorageStrategy | null => {
  const storages = getStorages();
  for (const storage of storages) {
    const strategy = readValue(storage, STORAGE_STRATEGY_KEY);
    if (strategy === "local" || strategy === "session") {
      return strategy;
    }
  }
  return null;
};

export const readStoredPermission = (): number | null => {
  const storages = getStorages();
  for (const storage of storages) {
    const raw = readValue(storage, PERMISSION_KEY);
    if (typeof raw === "string" && raw.trim().length > 0) {
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) {
        return Math.max(0, Math.floor(parsed));
      }
    }
  }
  return null;
};

export const readStoredUserId = (): string | null => {
  const storages = getStorages();
  for (const storage of storages) {
    const raw = readValue(storage, USER_ID_KEY);
    const userId = normalizeUserId(raw);
    if (userId !== null) {
      return userId;
    }
  }
  return null;
};

export const persistAuthState = (
  token: string,
  remember = true,
  permission?: number | null,
  userId?: string | number | null,
) => {
  const strategy: StorageStrategy = remember ? "local" : "session";
  const target = getStorage(strategy) ?? getStorage("local") ?? getStorage("session");
  if (!target) {
    return;
  }

  clearAuthState();

  const normalizedPermission = normalizePermission(permission);
  const normalizedUserId = normalizeUserId(userId);

  writeValue(target, TOKEN_STORAGE_KEYS[0], token);
  writeValue(target, AUTH_FLAG_KEY, "true");
  writeValue(target, STORAGE_STRATEGY_KEY, strategy);
  if (normalizedPermission !== null) {
    writeValue(target, PERMISSION_KEY, String(normalizedPermission));
  }
  if (normalizedUserId !== null) {
    writeValue(target, USER_ID_KEY, normalizedUserId);
  }

  dispatchAuthEvent();
};

export const clearAuthState = () => {
  const storages = getStorages();
  for (const storage of storages) {
    for (const key of TOKEN_STORAGE_KEYS) {
      removeValue(storage, key);
    }
    removeValue(storage, AUTH_FLAG_KEY);
    removeValue(storage, STORAGE_STRATEGY_KEY);
    removeValue(storage, PERMISSION_KEY);
    removeValue(storage, USER_ID_KEY);
  }
  dispatchAuthEvent();
};
